// ContractMeetingsSection.js
// ─────────────────────────────────────────────────────────────────────────────
// CONTRACT MEETINGS — monthly site/progress meeting register with live data
// snapshots, rolling action items, and AI-generated minutes.
//
// What it does
// ------------
//  • Register of meetings:  projects/{projectId}/meetings
//  • Each meeting captures: meta (no / date / venue / type), attendees,
//    a frozen DATA SNAPSHOT computed from live section data at the moment of
//    creation (refreshable), agenda discussion notes per standard heading,
//    and a rolling ACTION ITEMS register.
//  • Open action items from the previous meeting are carried forward
//    automatically on creation (flagged "c/f").
//  • "Generate Minutes" builds a structured prompt from the snapshot, notes
//    and actions, and routes it through the Firebase anthropicProxy
//    (same pattern as AIHelper.js — token-verified, never direct from browser).
//  • Print view renders a clean A4 minutes layout via a portal modal.
//
// Data sources (props — all already plumbed in App.js scope):
//   currentProject, certificates (certificatesWithCpa), variations, claims,
//   rfis, instructions, ncrs, diaries, programmeActivities, currentUser.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { appCheckHeader } from "../utils/appCheck";
import { tokens } from "./theme";

const C = tokens.color;
const MONO = '"JetBrains Mono", monospace';

// ─── Constants ───────────────────────────────────────────────────────────────

const MEETING_TYPES = ["Site Progress", "Technical", "Commercial", "Special"];

const MEETING_STATUS = {
  Draft:  { bg: C.neutralSoft, text: C.neutral,  border: C.neutralBorder },
  Issued: { bg: C.successSoft, text: C.success,  border: C.successBorder },
};

const ACTION_STATUS = {
  Open:   { bg: C.warningSoft, text: C.warning, border: C.warningBorder },
  Closed: { bg: C.successSoft, text: C.success, border: C.successBorder },
};

// Standard construction progress-meeting agenda. Notes are captured per heading.
const AGENDA = [
  { key: "previous",     label: "1. Previous Minutes & Matters Arising" },
  { key: "hs",           label: "2. Health & Safety" },
  { key: "environment",  label: "3. Environmental" },
  { key: "progress",     label: "4. Progress & Programme" },
  { key: "quality",      label: "5. Quality" },
  { key: "commercial",   label: "6. Commercial / Financial" },
  { key: "contractual",  label: "7. Contractual (VOs, Claims, RFIs, Instructions)" },
  { key: "general",      label: "8. General" },
];

const emptyAttendee = { name: "", organisation: "", role: "", present: true };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const numberOrZero = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtR = (v) =>
  "R " + numberOrZero(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtR0 = (v) =>
  "R " + numberOrZero(v).toLocaleString("en-ZA", { maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

function nextMeetingNo(meetings, type) {
  const prefix = type === "Site Progress" ? "SPM" :
                 type === "Technical"     ? "TM"  :
                 type === "Commercial"    ? "CM"  : "SM";
  const nums = meetings
    .filter((m) => (m.meetingNo || "").startsWith(prefix + "-"))
    .map((m) => parseInt((m.meetingNo || "").split("-")[1], 10))
    .filter((n) => Number.isFinite(n));
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `${prefix}-${String(next).padStart(3, "0")}`;
}

// ─── Data snapshot ───────────────────────────────────────────────────────────
// Frozen picture of the contract at meeting time. Computed from props, stored
// on the meeting doc, refreshable on demand. All reads are defensive — field
// names follow the conventions used in the source sections.

function buildSnapshot({
  currentProject, certificates, variations, claims,
  rfis, instructions, ncrs, diaries, programmeActivities, sinceDate,
}) {
  const today = todayISO();

  // ── Commercial ──
  const certs = [...(certificates || [])].sort(
    (a, b) => numberOrZero(a.certificateNo) - numberOrZero(b.certificateNo)
  );
  const latestCert = certs.length ? certs[certs.length - 1] : null;

  const approvedVOs = (variations || []).filter((v) => v.status === "Approved");
  const pendingVOs  = (variations || []).filter((v) => v.status !== "Approved");
  const voApprovedValue = approvedVOs.reduce((s, v) => s + numberOrZero(v.amount), 0);

  const openClaims = (claims || []).filter((c) => c.status !== "Awarded" && c.status !== "Rejected");
  const awardedClaims = (claims || []).filter((c) => c.status === "Awarded");

  // ── Contractual admin ──
  const openRfis = (rfis || []).filter((r) => r.status === "Open" || r.status === "Under Review");
  const openInstructions = (instructions || []).filter((i) => i.status && i.status !== "Closed" && i.status !== "Complied");
  const openNcrs = (ncrs || []).filter((n) => n.status === "Open");

  // ── Programme ──
  // Planned % is time-based per activity: (today − start) / (revisedEnd − start),
  // weighted by duration. Actual uses status (Completed = 100, In Progress = 50
  // unless a numeric progress field exists).
  let plannedWeighted = 0, actualWeighted = 0, totalDur = 0;
  const delayed = [];
  (programmeActivities || []).forEach((a) => {
    if (a.isSummaryTask) return;
    const dur = Math.max(numberOrZero(a.durationDays), 1);
    const start = a.plannedStart || "";
    const finish = a.revisedFinish || a.plannedFinish || "";
    let planned = 0;
    if (start && finish) {
      const s = new Date(start).getTime();
      const f = new Date(finish).getTime();
      const t = new Date(today).getTime();
      planned = f > s ? Math.min(100, Math.max(0, ((t - s) / (f - s)) * 100)) : (t >= s ? 100 : 0);
    }
    const explicit = numberOrZero(a.progress ?? a.pctComplete);
    const actual =
      a.status === "Completed" ? 100 :
      explicit > 0 ? Math.min(100, explicit) :
      a.status === "In Progress" ? 50 : 0;
    plannedWeighted += planned * dur;
    actualWeighted  += actual * dur;
    totalDur += dur;
    if (a.plannedFinish && a.revisedFinish && a.revisedFinish > a.plannedFinish) {
      delayed.push(a.name || a.activityId || "");
    }
  });
  const plannedPct = totalDur ? plannedWeighted / totalDur : 0;
  const actualPct  = totalDur ? actualWeighted / totalDur : 0;

  // ── Site ──
  const recentDiaries = (diaries || []).filter(
    (d) => !sinceDate || (d.diaryDate || "") >= sinceDate
  );

  return {
    capturedAt: today,
    sinceDate: sinceDate || "",
    contract: {
      number: currentProject?.contractNumber || "",
      description: currentProject?.contractDescription || "",
      contractor: currentProject?.contractor || "",
      client: currentProject?.client || "",
    },
    commercial: {
      latestCertNo: latestCert ? String(latestCert.certificateNo ?? "") : "",
      latestCertMonth: latestCert?.month || latestCert?.certificateMonth || "",
      latestCertSubtotalA: numberOrZero(latestCert?.effectiveSubtotalA ?? latestCert?.subtotalA),
      latestCertSubtotalB: numberOrZero(latestCert?.subtotalB),
      certificateCount: certs.length,
      voApprovedCount: approvedVOs.length,
      voApprovedValue,
      voPendingCount: pendingVOs.length,
      claimsOpenCount: openClaims.length,
      claimsAwardedCount: awardedClaims.length,
    },
    programme: {
      plannedPct: Math.round(plannedPct * 10) / 10,
      actualPct: Math.round(actualPct * 10) / 10,
      activityCount: (programmeActivities || []).filter((a) => !a.isSummaryTask).length,
      delayedCount: delayed.length,
      delayedSample: delayed.slice(0, 6),
    },
    admin: {
      openRfis: openRfis.length,
      openRfiRefs: openRfis.slice(0, 8).map((r) => r.rfiNo || r.ref || r.id),
      openInstructions: openInstructions.length,
      openNcrs: openNcrs.length,
      openNcrRefs: openNcrs.slice(0, 8).map((n) => n.ncrNo || n.ref || n.id),
    },
    site: {
      diariesSinceLast: recentDiaries.length,
      latestDiary: recentDiaries.length
        ? (recentDiaries.map((d) => d.diaryDate).sort().slice(-1)[0] || "")
        : "",
    },
  };
}

// ─── AI minutes call ─────────────────────────────────────────────────────────
// Routes through the same Firebase proxy as AIHelper.js but with a higher
// max_tokens budget — minutes are long-form output.

const AI_PROXY_URL = (process.env.REACT_APP_AI_PROXY_URL || "").trim();

async function generateMinutesAI(meeting, snapshot) {
  if (!AI_PROXY_URL) {
    throw new Error("AI proxy not configured. Set REACT_APP_AI_PROXY_URL in .env.local.");
  }

  const agendaNotes = AGENDA
    .map((a) => `${a.label}\n${(meeting.agendaNotes?.[a.key] || "").trim() || "(no notes captured)"}`)
    .join("\n\n");

  const actionsBlock = (meeting.actionItems || [])
    .map((it) => `- [${it.status}] ${it.ref}: ${it.description} (Responsible: ${it.responsible || "—"}, Due: ${it.dueDate || "—"})${it.carriedForward ? " [carried forward]" : ""}`)
    .join("\n") || "(no action items)";

  const attendeesBlock = (meeting.attendees || [])
    .map((a) => `- ${a.name} (${a.organisation}${a.role ? ", " + a.role : ""}) — ${a.present ? "Present" : "Apology"}`)
    .join("\n") || "(none captured)";

  const system =
    "You are a professional construction contract administrator (quantity surveyor) in South Africa, " +
    "writing formal minutes of a contract progress meeting under a FIDIC/GCC framework. " +
    "Write in clear, formal, third-person minute style. Use the numbered agenda structure given. " +
    "All monetary values are in South African Rand (ZAR). Do not invent facts — only use the data " +
    "and notes provided. Where a heading has no notes, summarise the relevant snapshot data instead. " +
    "End with the action items table summary and the proposed date of the next meeting if given. " +
    "Output plain text with numbered headings — no markdown symbols like ** or #.";

  const user =
`MEETING DETAILS
Meeting No: ${meeting.meetingNo}
Type: ${meeting.meetingType}
Date: ${meeting.meetingDate}
Venue: ${meeting.venue || "—"}
Contract: ${snapshot?.contract?.number || ""} — ${snapshot?.contract?.description || ""}
Contractor: ${snapshot?.contract?.contractor || ""}
Client / Employer: ${snapshot?.contract?.client || ""}
Next meeting: ${meeting.nextMeetingDate || "to be confirmed"}

ATTENDANCE
${attendeesBlock}

DATA SNAPSHOT (live contract data captured ${snapshot?.capturedAt || ""})
Commercial:
- Latest payment certificate: No. ${snapshot?.commercial?.latestCertNo || "—"} (${snapshot?.commercial?.latestCertMonth || "—"}), Subtotal A ${fmtR(snapshot?.commercial?.latestCertSubtotalA)}, Subtotal B ${fmtR(snapshot?.commercial?.latestCertSubtotalB)}
- Certificates issued to date: ${snapshot?.commercial?.certificateCount ?? 0}
- Variation orders: ${snapshot?.commercial?.voApprovedCount ?? 0} approved (${fmtR(snapshot?.commercial?.voApprovedValue)}), ${snapshot?.commercial?.voPendingCount ?? 0} pending
- Claims: ${snapshot?.commercial?.claimsOpenCount ?? 0} open, ${snapshot?.commercial?.claimsAwardedCount ?? 0} awarded
Programme:
- Planned progress ${snapshot?.programme?.plannedPct ?? 0}% vs actual ${snapshot?.programme?.actualPct ?? 0}% (${snapshot?.programme?.activityCount ?? 0} activities)
- Delayed activities: ${snapshot?.programme?.delayedCount ?? 0}${(snapshot?.programme?.delayedSample || []).length ? " — e.g. " + snapshot.programme.delayedSample.join("; ") : ""}
Contract administration:
- Open RFIs: ${snapshot?.admin?.openRfis ?? 0}${(snapshot?.admin?.openRfiRefs || []).length ? " (" + snapshot.admin.openRfiRefs.join(", ") + ")" : ""}
- Open instructions: ${snapshot?.admin?.openInstructions ?? 0}
- Open NCRs: ${snapshot?.admin?.openNcrs ?? 0}${(snapshot?.admin?.openNcrRefs || []).length ? " (" + snapshot.admin.openNcrRefs.join(", ") + ")" : ""}
Site:
- Site diaries since last meeting: ${snapshot?.site?.diariesSinceLast ?? 0} (latest ${snapshot?.site?.latestDiary || "—"})

DISCUSSION NOTES PER AGENDA ITEM
${agendaNotes}

ACTION ITEMS
${actionsBlock}

Please draft the full formal minutes now.`;

  let idToken = "";
  try {
    const { getAuth } = await import("firebase/auth");
    const u = getAuth().currentUser;
    if (u) idToken = await u.getIdToken();
  } catch (_) { /* dev without auth */ }

  const res = await fetch(AI_PROXY_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(await appCheckHeader()),
    },
    body: JSON.stringify({
      model: "claude-sonnet-4-5",
      max_tokens: 4000,
      system,
      messages: [{ role: "user", content: user }],
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || err?.error || `AI proxy HTTP ${res.status}`);
  }
  const data = await res.json();
  return (data.content || [])
    .filter((b) => b.type === "text")
    .map((b) => b.text)
    .join("\n")
    .trim();
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const s = {
  page: { display: "grid", gap: 20, minWidth: 0, fontFamily: tokens.font?.body || '"Inter", sans-serif', color: C.ink },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20, boxShadow: tokens.shadow?.sm, minWidth: 0 },
  title: { margin: 0, fontSize: 24, fontFamily: tokens.font?.display, color: C.ink, letterSpacing: "-0.02em" },
  sub: { margin: "4px 0 0", fontSize: 12.5, color: C.inkMuted },
  h3: { margin: "0 0 10px", fontSize: 13, fontWeight: 800, color: C.ink, letterSpacing: "-0.01em" },
  lbl: { display: "block", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, marginBottom: 4 },
  inp: { width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, fontFamily: "inherit", outline: "none" },
  ta:  { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 13, color: C.ink, fontFamily: "inherit", outline: "none", resize: "vertical", lineHeight: 1.55 },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.ink, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnPrimary: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.primary, color: C.inkOnDark, fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnAccent: { padding: "8px 14px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" },
  btnDanger: { padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.dangerBorder}`, background: C.dangerSoft, color: C.danger, fontSize: 11.5, fontWeight: 700, cursor: "pointer" },
  th: { padding: "8px 10px", textAlign: "left", fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.inkMuted, borderBottom: `2px solid ${C.border}`, background: C.surfaceAlt, whiteSpace: "nowrap" },
  td: { padding: "8px 10px", fontSize: 12.5, borderBottom: `1px solid ${C.border}`, verticalAlign: "top" },
  chip: (m) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: "0.04em", background: m.bg, color: m.text, border: `1px solid ${m.border}` }),
  mono: { fontFamily: MONO, fontVariantNumeric: "tabular-nums" },
  statTile: { background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", minWidth: 0 },
  statLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.inkMuted, margin: 0 },
  statValue: { fontSize: 16, fontWeight: 800, fontFamily: MONO, color: C.ink, margin: "2px 0 0" },
};

// ─── Snapshot panel ──────────────────────────────────────────────────────────

function SnapshotPanel({ snapshot, onRefresh }) {
  if (!snapshot) return null;
  const sn = snapshot;
  const tiles = [
    { label: "Latest Cert", value: sn.commercial?.latestCertNo ? `No. ${sn.commercial.latestCertNo}` : "—" },
    { label: "Subtotal B", value: fmtR0(sn.commercial?.latestCertSubtotalB) },
    { label: "VOs Approved", value: `${sn.commercial?.voApprovedCount ?? 0} · ${fmtR0(sn.commercial?.voApprovedValue)}` },
    { label: "Claims Open", value: String(sn.commercial?.claimsOpenCount ?? 0) },
    { label: "Planned %", value: `${sn.programme?.plannedPct ?? 0}%` },
    { label: "Actual %", value: `${sn.programme?.actualPct ?? 0}%` },
    { label: "Delayed Acts", value: String(sn.programme?.delayedCount ?? 0) },
    { label: "Open RFIs", value: String(sn.admin?.openRfis ?? 0) },
    { label: "Open NCRs", value: String(sn.admin?.openNcrs ?? 0) },
    { label: "Diaries Since Last", value: String(sn.site?.diariesSinceLast ?? 0) },
  ];
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <h3 style={{ ...s.h3, margin: 0 }}>
          Data Snapshot
          <span style={{ fontWeight: 600, color: C.inkMuted, fontSize: 11, marginLeft: 8 }}>
            captured {fmtDate(sn.capturedAt)}{sn.sinceDate ? ` · window from ${fmtDate(sn.sinceDate)}` : ""}
          </span>
        </h3>
        <button style={s.btn} onClick={onRefresh}>↻ Refresh Snapshot</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        {tiles.map((t) => (
          <div key={t.label} style={s.statTile}>
            <p style={s.statLabel}>{t.label}</p>
            <p style={s.statValue}>{t.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Print modal ─────────────────────────────────────────────────────────────

function PrintMinutesModal({ meeting, snapshot, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 4000, background: "rgba(14,17,22,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "32px 16px" }}>
      <div className="cms-print-sheet" style={{ background: "#fff", width: "min(840px, 100%)", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,0.35)", padding: "40px 48px", color: "#0E1116", fontFamily: '"Inter", sans-serif' }}>
        <div className="cms-print-hide" style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 18 }}>
          <button style={s.btnPrimary} onClick={() => window.print()}>🖨 Print / Save PDF</button>
          <button style={s.btn} onClick={onClose}>Close</button>
        </div>

        <div style={{ borderBottom: `3px solid ${C.primary}`, paddingBottom: 14, marginBottom: 18 }}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.primary }}>Minutes of Meeting</p>
          <h1 style={{ margin: "4px 0 2px", fontSize: 22, letterSpacing: "-0.02em" }}>
            {meeting.meetingType} Meeting <span style={{ ...s.mono }}>{meeting.meetingNo}</span>
          </h1>
          <p style={{ margin: 0, fontSize: 12.5, color: "#444" }}>
            {snapshot?.contract?.number} — {snapshot?.contract?.description}
          </p>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 18 }}>
          <tbody>
            <tr>
              <td style={{ padding: "4px 0", width: "20%", fontWeight: 700 }}>Date</td>
              <td style={{ padding: "4px 0", width: "30%" }}>{fmtDate(meeting.meetingDate)}</td>
              <td style={{ padding: "4px 0", width: "20%", fontWeight: 700 }}>Venue</td>
              <td style={{ padding: "4px 0" }}>{meeting.venue || "—"}</td>
            </tr>
            <tr>
              <td style={{ padding: "4px 0", fontWeight: 700 }}>Contractor</td>
              <td style={{ padding: "4px 0" }}>{snapshot?.contract?.contractor || "—"}</td>
              <td style={{ padding: "4px 0", fontWeight: 700 }}>Employer</td>
              <td style={{ padding: "4px 0" }}>{snapshot?.contract?.client || "—"}</td>
            </tr>
            <tr>
              <td style={{ padding: "4px 0", fontWeight: 700 }}>Next Meeting</td>
              <td style={{ padding: "4px 0" }} colSpan={3}>{meeting.nextMeetingDate ? fmtDate(meeting.nextMeetingDate) : "To be confirmed"}</td>
            </tr>
          </tbody>
        </table>

        {(meeting.attendees || []).length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: 13, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Attendance</h2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Name", "Organisation", "Role", "Status"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "5px 8px", borderBottom: "1.5px solid #0E1116" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(meeting.attendees || []).map((a, i) => (
                  <tr key={i}>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{a.name}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{a.organisation}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{a.role}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{a.present ? "Present" : "Apology"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ whiteSpace: "pre-wrap", fontSize: 12.5, lineHeight: 1.65, marginBottom: 22 }}>
          {meeting.minutesText || "No minutes generated yet."}
        </div>

        {(meeting.actionItems || []).length > 0 && (
          <div>
            <h2 style={{ fontSize: 13, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Action Items</h2>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  {["Ref", "Action", "Responsible", "Due", "Status"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "5px 8px", borderBottom: "1.5px solid #0E1116" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(meeting.actionItems || []).map((it) => (
                  <tr key={it.id}>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd", fontFamily: MONO }}>{it.ref}{it.carriedForward ? " (c/f)" : ""}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{it.description}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{it.responsible}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{it.dueDate ? fmtDate(it.dueDate) : "—"}</td>
                    <td style={{ padding: "4px 8px", borderBottom: "1px solid #ddd" }}>{it.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <style>{`
          @media print {
            body * { visibility: hidden; }
            .cms-print-sheet, .cms-print-sheet * { visibility: visible; }
            .cms-print-sheet { position: absolute; left: 0; top: 0; width: 100%; box-shadow: none !important; border-radius: 0 !important; padding: 0 !important; }
            .cms-print-hide { display: none !important; }
          }
        `}</style>
      </div>
    </div>,
    document.body
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function ContractMeetingsSection({
  currentProject,
  currentProjectId,
  currentUser,
  certificates = [],
  variations = [],
  claims = [],
  rfis = [],
  instructions = [],
  ncrs = [],
  diaries = [],
  programmeActivities = [],
}) {
  const projectId = currentProject?.id || currentProjectId;

  const [meetings, setMeetings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState("");
  const [printOpen, setPrintOpen] = useState(false);
  const [savingFlash, setSavingFlash] = useState(false);

  // Local editable copy of the selected meeting (debounced-style explicit save)
  const [draft, setDraft] = useState(null);
  const dirtyRef = useRef(false);

  // ── Subscription ──
  useEffect(() => {
    if (!projectId) return;
    const q = query(collection(db, "projects", projectId, "meetings"), orderBy("meetingDate", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setMeetings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [projectId]);

  const selected = useMemo(
    () => meetings.find((m) => m.id === selectedId) || null,
    [meetings, selectedId]
  );

  // Sync draft when selecting a different meeting (don't clobber unsaved edits on snapshot ticks)
  useEffect(() => {
    if (!selected) { setDraft(null); dirtyRef.current = false; return; }
    if (!draft || draft.id !== selected.id) {
      setDraft(JSON.parse(JSON.stringify(selected)));
      dirtyRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const setField = useCallback((patch) => {
    dirtyRef.current = true;
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }, []);

  // ── Audit log ──
  const logAudit = useCallback(async (action, entityId, summary) => {
    if (!projectId) return;
    try {
      await addDoc(collection(db, "projects", projectId, "auditLogs"), {
        entityType: "meeting",
        entityId: entityId || "",
        action,
        summary: summary || "",
        userName: currentUser?.displayName || "",
        userEmail: currentUser?.email || "",
        createdAt: serverTimestamp(),
      });
    } catch (e) { /* best-effort */ }
  }, [projectId, currentUser]);

  // ── Snapshot builder bound to live props ──
  const computeSnapshot = useCallback((sinceDate) =>
    buildSnapshot({
      currentProject, certificates, variations, claims,
      rfis, instructions, ncrs, diaries, programmeActivities, sinceDate,
    }),
    [currentProject, certificates, variations, claims, rfis, instructions, ncrs, diaries, programmeActivities]
  );

  // ── Create meeting ──
  const createMeeting = useCallback(async (type) => {
    if (!projectId) return;
    const sorted = [...meetings].sort((a, b) => (a.meetingDate || "").localeCompare(b.meetingDate || ""));
    const previous = sorted.length ? sorted[sorted.length - 1] : null;

    // Carry forward open action items from the most recent meeting
    const carried = (previous?.actionItems || [])
      .filter((it) => it.status === "Open")
      .map((it) => ({ ...it, carriedForward: true }));

    const meetingNo = nextMeetingNo(meetings, type);
    const snapshot = computeSnapshot(previous?.meetingDate || "");

    const docData = {
      meetingNo,
      meetingType: type,
      meetingDate: todayISO(),
      venue: "",
      nextMeetingDate: "",
      status: "Draft",
      attendees: [],
      agendaNotes: {},
      actionItems: carried,
      snapshot,
      minutesText: "",
      createdAt: serverTimestamp(),
      createdBy: currentUser?.email || "",
    };
    const ref = await addDoc(collection(db, "projects", projectId, "meetings"), docData);
    await logAudit("create", ref.id, `Meeting ${meetingNo} (${type}) created${carried.length ? `, ${carried.length} action item(s) carried forward` : ""}`);
    setSelectedId(ref.id);
  }, [projectId, meetings, computeSnapshot, currentUser, logAudit]);

  // ── Save draft ──
  const saveDraft = useCallback(async () => {
    if (!projectId || !draft) return;
    const { id, createdAt, ...rest } = draft;
    await updateDoc(doc(db, "projects", projectId, "meetings", id), {
      ...rest,
      updatedAt: serverTimestamp(),
    });
    dirtyRef.current = false;
    setSavingFlash(true);
    setTimeout(() => setSavingFlash(false), 1200);
  }, [projectId, draft]);

  // ── Delete ──
  const deleteMeeting = useCallback(async (m) => {
    if (!projectId || !m) return;
    if (!window.confirm(`Delete meeting ${m.meetingNo}? This cannot be undone.`)) return;
    await deleteDoc(doc(db, "projects", projectId, "meetings", m.id));
    await logAudit("delete", m.id, `Meeting ${m.meetingNo} deleted`);
    if (selectedId === m.id) setSelectedId(null);
  }, [projectId, selectedId, logAudit]);

  // ── Refresh snapshot on the open draft ──
  const refreshSnapshot = useCallback(() => {
    if (!draft) return;
    setField({ snapshot: computeSnapshot(draft.snapshot?.sinceDate || "") });
  }, [draft, computeSnapshot, setField]);

  // ── Attendees ──
  const updateAttendee = (idx, patch) => {
    const next = [...(draft.attendees || [])];
    next[idx] = { ...next[idx], ...patch };
    setField({ attendees: next });
  };
  const addAttendee = () => setField({ attendees: [...(draft.attendees || []), { ...emptyAttendee }] });
  const removeAttendee = (idx) => setField({ attendees: (draft.attendees || []).filter((_, i) => i !== idx) });

  // ── Action items ──
  const nextActionRef = () => {
    const nums = (draft.actionItems || [])
      .map((it) => parseInt((it.ref || "").split("/").pop(), 10))
      .filter((n) => Number.isFinite(n));
    const n = nums.length ? Math.max(...nums) + 1 : 1;
    return `${draft.meetingNo}/${String(n).padStart(2, "0")}`;
  };
  const addAction = () =>
    setField({
      actionItems: [
        ...(draft.actionItems || []),
        { id: `a_${Date.now().toString(36)}`, ref: nextActionRef(), description: "", responsible: "", dueDate: "", status: "Open", carriedForward: false },
      ],
    });
  const updateAction = (id, patch) =>
    setField({ actionItems: (draft.actionItems || []).map((it) => (it.id === id ? { ...it, ...patch } : it)) });
  const removeAction = (id) =>
    setField({ actionItems: (draft.actionItems || []).filter((it) => it.id !== id) });

  // ── AI generation ──
  const runGenerate = useCallback(async () => {
    if (!draft) return;
    setGenerating(true);
    setAiError("");
    try {
      const text = await generateMinutesAI(draft, draft.snapshot);
      setField({ minutesText: text });
    } catch (e) {
      setAiError(e.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [draft, setField]);

  if (!projectId) {
    return (
      <section style={s.card}>
        <h2 style={s.title}>Contract Meetings</h2>
        <p style={s.sub}>Select a project to manage meetings.</p>
      </section>
    );
  }

  const openActions = meetings.reduce(
    (n, m) => n + (m.actionItems || []).filter((it) => it.status === "Open").length, 0
  );

  return (
    <div style={s.page}>
      {/* ── Header + register ── */}
      <section style={s.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={s.title}>Contract Meetings</h2>
            <p style={s.sub}>
              Progress meeting register with live data snapshots, rolling action items, and AI-drafted minutes.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {MEETING_TYPES.map((t) => (
              <button key={t} style={t === "Site Progress" ? s.btnAccent : s.btn} onClick={() => createMeeting(t)}>
                + {t}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
          <div style={s.statTile}><p style={s.statLabel}>Meetings</p><p style={s.statValue}>{meetings.length}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Open Action Items</p><p style={s.statValue}>{openActions}</p></div>
          <div style={s.statTile}><p style={s.statLabel}>Last Meeting</p><p style={s.statValue}>{meetings.length ? fmtDate(meetings[0].meetingDate) : "—"}</p></div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Meeting No", "Type", "Date", "Venue", "Actions Open", "Status", ""].map((h) => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {meetings.length === 0 ? (
                <tr><td colSpan={7} style={{ ...s.td, textAlign: "center", color: C.inkMuted, padding: 24 }}>
                  No meetings yet — create the first one above.
                </td></tr>
              ) : meetings.map((m) => {
                const open = (m.actionItems || []).filter((it) => it.status === "Open").length;
                const isSel = m.id === selectedId;
                return (
                  <tr key={m.id}
                      onClick={() => setSelectedId(isSel ? null : m.id)}
                      style={{ cursor: "pointer", background: isSel ? C.primarySoft : "transparent" }}>
                    <td style={{ ...s.td, ...s.mono, fontWeight: 800, color: C.primary }}>{m.meetingNo}</td>
                    <td style={s.td}>{m.meetingType}</td>
                    <td style={{ ...s.td, ...s.mono }}>{fmtDate(m.meetingDate)}</td>
                    <td style={s.td}>{m.venue || "—"}</td>
                    <td style={{ ...s.td, ...s.mono }}>{open}</td>
                    <td style={s.td}>
                      <span style={s.chip(MEETING_STATUS[m.status] || MEETING_STATUS.Draft)}>{m.status || "Draft"}</span>
                    </td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>
                      <button style={s.btnDanger} onClick={(e) => { e.stopPropagation(); deleteMeeting(m); }}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Detail ── */}
      {draft && (
        <section style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <h2 style={{ ...s.title, fontSize: 19 }}>
              <span style={{ ...s.mono, color: C.primary }}>{draft.meetingNo}</span>
              <span style={{ fontWeight: 600, color: C.inkMuted, fontSize: 13, marginLeft: 10 }}>{draft.meetingType} Meeting</span>
            </h2>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {savingFlash && <span style={{ fontSize: 11.5, fontWeight: 700, color: C.success }}>✓ Saved</span>}
              <button style={s.btn} onClick={() => setPrintOpen(true)}>🖨 Print View</button>
              <button
                style={s.btn}
                onClick={() => setField({ status: draft.status === "Issued" ? "Draft" : "Issued" })}
              >
                {draft.status === "Issued" ? "↩ Revert to Draft" : "✓ Mark Issued"}
              </button>
              <button style={s.btnPrimary} onClick={saveDraft}>Save Meeting</button>
            </div>
          </div>

          {/* Meta */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 20, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Meeting Date</label>
              <input type="date" style={s.inp} value={draft.meetingDate || ""} onChange={(e) => setField({ meetingDate: e.target.value })} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Venue</label>
              <input style={s.inp} value={draft.venue || ""} placeholder="e.g. Site office, Main Road" onChange={(e) => setField({ venue: e.target.value })} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Next Meeting</label>
              <input type="date" style={s.inp} value={draft.nextMeetingDate || ""} onChange={(e) => setField({ nextMeetingDate: e.target.value })} />
            </div>
            <div style={{ minWidth: 0 }}>
              <label style={s.lbl}>Type</label>
              <select style={s.inp} value={draft.meetingType || "Site Progress"} onChange={(e) => setField({ meetingType: e.target.value })}>
                {MEETING_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Snapshot */}
          <div style={{ marginBottom: 22 }}>
            <SnapshotPanel snapshot={draft.snapshot} onRefresh={refreshSnapshot} />
          </div>

          {/* Attendees */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ ...s.h3, margin: 0 }}>Attendance</h3>
              <button style={s.btn} onClick={addAttendee}>+ Add Attendee</button>
            </div>
            {(draft.attendees || []).length === 0 ? (
              <p style={{ ...s.sub, margin: 0 }}>No attendees captured yet.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>{["Name", "Organisation", "Role", "Present", ""].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(draft.attendees || []).map((a, i) => (
                      <tr key={i}>
                        <td style={s.td}><input style={s.inp} value={a.name} onChange={(e) => updateAttendee(i, { name: e.target.value })} /></td>
                        <td style={s.td}><input style={s.inp} value={a.organisation} onChange={(e) => updateAttendee(i, { organisation: e.target.value })} /></td>
                        <td style={s.td}><input style={s.inp} value={a.role} onChange={(e) => updateAttendee(i, { role: e.target.value })} /></td>
                        <td style={{ ...s.td, textAlign: "center" }}>
                          <input type="checkbox" checked={!!a.present} onChange={(e) => updateAttendee(i, { present: e.target.checked })} />
                        </td>
                        <td style={s.td}><button style={s.btnDanger} onClick={() => removeAttendee(i)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Agenda notes */}
          <div style={{ marginBottom: 22 }}>
            <h3 style={s.h3}>Discussion Notes</h3>
            <div style={{ display: "grid", gap: 12 }}>
              {AGENDA.map((a) => (
                <div key={a.key} style={{ minWidth: 0 }}>
                  <label style={s.lbl}>{a.label}</label>
                  <textarea
                    rows={2}
                    style={s.ta}
                    placeholder="Key points discussed…"
                    value={draft.agendaNotes?.[a.key] || ""}
                    onChange={(e) => setField({ agendaNotes: { ...(draft.agendaNotes || {}), [a.key]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Action items */}
          <div style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ ...s.h3, margin: 0 }}>
                Action Items
                <span style={{ fontWeight: 600, color: C.inkMuted, fontSize: 11, marginLeft: 8 }}>
                  open items roll forward to the next meeting automatically
                </span>
              </h3>
              <button style={s.btn} onClick={addAction}>+ Add Action</button>
            </div>
            {(draft.actionItems || []).length === 0 ? (
              <p style={{ ...s.sub, margin: 0 }}>No action items.</p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>{["Ref", "Action", "Responsible", "Due", "Status", ""].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(draft.actionItems || []).map((it) => (
                      <tr key={it.id} style={{ background: it.carriedForward ? C.warningSoft : "transparent" }}>
                        <td style={{ ...s.td, ...s.mono, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {it.ref}{it.carriedForward && <span title="Carried forward" style={{ marginLeft: 6, fontSize: 10, color: C.warning }}>c/f</span>}
                        </td>
                        <td style={{ ...s.td, minWidth: 220 }}>
                          <input style={s.inp} value={it.description} onChange={(e) => updateAction(it.id, { description: e.target.value })} />
                        </td>
                        <td style={s.td}>
                          <input style={{ ...s.inp, minWidth: 120 }} value={it.responsible} onChange={(e) => updateAction(it.id, { responsible: e.target.value })} />
                        </td>
                        <td style={s.td}>
                          <input type="date" style={{ ...s.inp, minWidth: 130 }} value={it.dueDate || ""} onChange={(e) => updateAction(it.id, { dueDate: e.target.value })} />
                        </td>
                        <td style={s.td}>
                          <button
                            style={{ ...s.chip(ACTION_STATUS[it.status] || ACTION_STATUS.Open), cursor: "pointer", border: `1px solid ${(ACTION_STATUS[it.status] || ACTION_STATUS.Open).border}` }}
                            onClick={() => updateAction(it.id, { status: it.status === "Open" ? "Closed" : "Open" })}
                            title="Toggle status"
                          >
                            {it.status}
                          </button>
                        </td>
                        <td style={s.td}><button style={s.btnDanger} onClick={() => removeAction(it.id)}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* AI minutes */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 10, flexWrap: "wrap" }}>
              <h3 style={{ ...s.h3, margin: 0 }}>Minutes Write-Up</h3>
              <button style={s.btnAccent} onClick={runGenerate} disabled={generating}>
                {generating ? "Generating…" : "✦ Generate Minutes with AI"}
              </button>
            </div>
            {aiError && (
              <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: C.danger }}>{aiError}</p>
            )}
            <textarea
              rows={14}
              style={{ ...s.ta, fontSize: 12.5 }}
              placeholder="Generate with AI or write the minutes manually. The text here is what appears on the print view."
              value={draft.minutesText || ""}
              onChange={(e) => setField({ minutesText: e.target.value })}
            />
            <p style={{ ...s.sub, marginTop: 6 }}>
              Generation is drafted from the snapshot, your discussion notes, and the action register — review and edit before issuing. Remember to <strong>Save Meeting</strong>.
            </p>
          </div>
        </section>
      )}

      {printOpen && draft && (
        <PrintMinutesModal meeting={draft} snapshot={draft.snapshot} onClose={() => setPrintOpen(false)} />
      )}
    </div>
  );
}

export default React.memo(ContractMeetingsSection);
